/**
 * Live API adapter for the YVR frontend (added 2026-09-23).
 * Implements the same contract as the IndexedDB mock adapter in api.js,
 * but calls the FastAPI backend. Offline writes go through the existing sync queue.
 * Data shape: converts backend snake_case docs to frontend camelCase domain shape.
 */
const API_BASE = (window.__YVR_API_BASE__ || '').replace(/\/$/, '');

function token() {
  try { return JSON.parse(localStorage.getItem('yvr_token') || 'null'); } catch { return null; }
}
export function setToken(t) { t ? localStorage.setItem('yvr_token', JSON.stringify(t)) : localStorage.removeItem('yvr_token'); }

async function request(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const t = token();
  if (t) headers['Authorization'] = 'Bearer ' + t.token;
  const res = await fetch(API_BASE + path, { method, headers, body: method === 'GET' ? undefined : JSON.stringify(body) });
  if (!res.ok) {
    let msg = 'Request failed (' + res.status + ')';
    try { const j = await res.json(); msg = j.detail && typeof j.detail === 'string' ? j.detail : (j.detail?.[0]?.msg || msg); } catch {}
    throw new Error(msg);
  }
  return res.json();
}

// ---- shape converters: backend (snake) -> frontend domain (camel) ----
function mapUser(u) { return u ? { id: u.id, name: u.name, email: u.email, role: u.role || 'participant', sample: false, weeklyTargetKm: u.weeklyTargetKm || 20, consent: u.consent } : null; }

function mapChallenge(c) {
  const wps = (c.checkpoints || []).map((w, i) => ({
    id: w.id, name: w.name, kmFromStart: w.distance_km, lat: w.lat, lng: w.lon,
    description: w.description || '', rewardText: w.reward_text || '', rewardImage: w.reward_image || '',
  }));
  return {
    id: c.id, title: c.name, description: c.description, awardText: c.award || '',
    totalDistanceKmOverride: c.target_km, authorId: c.author_id || null,
    tabs: {
      ebib: c.ebib || '', certificateOfCompletion: c.certificate || '', digitalMedal: c.medal || '',
      mapCoordsAndType: { routeType: c.checkpoint_mode || 'virtual', coordinates: wps.map(w => ({ lat: w.lat, lng: w.lng })) },
      waypoints: wps, individualsWaypoints: [], startFinishPics: { banner: c.cover || '', start: c.start_image || '', finish: c.finish_image || '' },
    },
    app: { status: c.status || 'published', activities: c.activities || ['walk'], createdAt: c.created_at, sample: !!c.reference },
    wp: { wpPostId: c.wp_id || null, mastered: true, mappingStatus: c.wp_id ? 'read-only-public' : 'app' },
  };
}

// ---- session ----
export async function getSession() {
  const t = token();
  if (!t || t.expiresAt < Date.now()) return null;
  try { const me = await request('/api/auth/me'); return mapUser(me); }
  catch (e) {
    // 401 = token genuinely invalid -> clear. Network errors -> keep token (remember me survives flaky connections)
    if (/401|Session expired/i.test(e.message)) { localStorage.removeItem('yvr_token'); return null; }
    throw e;
  }
}

export async function loginUser({ email, password }) {
  const r = await request('/api/auth/login', { method: 'POST', body: { email, password } });
  const days = (()=>{try{return localStorage.getItem('yvr_session_days')==='1'?1:90;}catch{return 90;}})();
  setToken({ token: r.token, expiresAt: Date.now() + days * 86400000 });
  return mapUser(r.user);
}

export async function registerUser({ email, password, name = '', consent }) {
  if (!consent) throw new Error('Accept the privacy and GDPR consent checkbox.');
  if (password.length < 8) throw new Error('Use at least 8 characters for your password.');
  const r = await request('/api/auth/register', { method: 'POST', body: { email, password, name: name || email.split('@')[0], consent } });
  setToken({ token: r.token, expiresAt: Date.now() + 90 * 86400000 });
  return mapUser(r.user);
}

export async function logoutUser() { setToken(null); }

// ---- challenges (WP-mastered, server is source) ----
function mapProgress(p, challenge) {
  // ALWAYS return a full object - workspace components read .progress.percent etc. without null checks
  const wps = (challenge?.checkpoints || []).map(w => ({
    id: w.id, name: w.name, kmFromStart: w.distance_km, lat: w.lat, lng: w.lon, description: w.description || '',
  }));
  const unlockedIds = new Set(Object.keys(p?.unlocked || {}));
  return {
    distanceKm: p?.distance_km ?? 0,
    percent: p?.percent ?? 0,
    complete: !!p?.complete,
    activityCount: p?.activity_count ?? 0,
    remainingKm: p?.remaining_km ?? (challenge?.target_km ?? 0),
    unlocked: wps.filter(w => unlockedIds.has(w.id)),
    unlockedWaypoints: Array.from(unlockedIds),
    bestTimeS: p?.best_time_s ?? null,
  };
}

export async function getChallenges({ managed = false } = {}) {
  const me = await getSession();
  const list = await request('/api/challenges' + (me ? '?user_id=' + me.id : ''));
  const mapped = list.map(c => ({ ...mapChallenge(c), entryCount: c.entry_count || 0, pendingCount: c.pending_count || 0, joined: !!(c.membership), rulesLocked: !!c.rules_locked, progress: mapProgress(c.progress, c) }));
  return managed ? mapped.filter(c => me && ['admin', 'superadmin'].includes(me.role) || (me && c.authorId === me.id)) : mapped.filter(c => c.app.status !== 'draft');
}

export async function getChallenge(id, { userId } = {}) {
  const me = await getSession();
  const c = await request('/api/challenges/' + encodeURIComponent(id) + (me ? '?user_id=' + (userId || me.id) : ''));
  const m = mapChallenge(c);
  return { ...m, entryCount: c.entry_count || 0, joined: !!(c.membership), rulesLocked: !!c.rules_locked, progress: mapProgress(c.progress, c), participant: me ? mapUser(me) : null, journey: [] };
}
export const getWaypoints = async id => (await getChallenge(id)).tabs.waypoints;

// ---- mutations (queued offline, sent live when online) ----
async function liveMutation(path, method, body) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) throw new Error('Offline — change queued and will sync when you reconnect.');
  return request(path, { method, body });
}
export const createChallenge = c => liveMutation('/api/challenges', 'POST', {
  name: c.title, description: c.description || '', award: c.awardText || '', target_km: c.totalDistanceKmOverride,
  activities: c.app?.activities || ['walk'], checkpoint_mode: 'virtual',
  checkpoints: (c.tabs?.waypoints || []).map((w, i) => ({ id: w.id, name: w.name, distance_km: w.kmFromStart, lat: w.lat, lon: w.lng, description: w.description || '', order: i + 1 })),
  cover: c.tabs?.startFinishPics?.banner || '', status: c.app?.status || 'draft', author_id: c.authorId,
});
export const updateChallenge = c => liveMutation('/api/challenges/' + encodeURIComponent(c.id), 'PUT', {
  name: c.title, description: c.description || '', award: c.awardText || '', target_km: c.totalDistanceKmOverride,
  activities: c.app?.activities || ['walk'], checkpoint_mode: 'virtual',
  checkpoints: (c.tabs?.waypoints || []).map((w, i) => ({ id: w.id, name: w.name, distance_km: w.kmFromStart, lat: w.lat, lon: w.lng, description: w.description || '', order: i + 1 })),
  cover: c.tabs?.startFinishPics?.banner || '', status: c.app?.status || 'draft',
});
export const archiveChallenge = id => liveMutation('/api/challenges/' + encodeURIComponent(id) + '/archive', 'POST', {});
export const deleteChallenge = id => request('/api/challenges/' + encodeURIComponent(id), { method: 'DELETE' });
export async function joinChallenge(challengeId) {
  const me = await getSession();
  if (!me) throw new Error('Sign in first.');
  return liveMutation('/api/entries/' + encodeURIComponent(challengeId), 'POST', { user_id: me.id });
}

// ---- activities ----
export async function recordActivity(activity) {
  const me = await getSession();
  if (activity.source === 'gps' || activity.source === 'motion') {
    // ensure the session exists server-side (idempotent), then push the final snapshot
    try { await request('/api/sessions', { method: 'POST', body: { id: activity.id, user_id: me.id, challenge_id: activity.challengeId, activity_type: activity.type || 'walk' } }); }
    catch (e) { if (!/exists|409/i.test(e.message)) { /* already created is fine */ } }
    return liveMutation('/api/sessions/' + encodeURIComponent(activity.id), 'PUT', {
      user_id: me.id, revision: activity.revision || 1, points: (activity.points || []).map((p, i) => ({ seq: p.seq ?? i, lat: p.lat, lon: p.lng ?? p.lon, accuracy: p.accuracy ?? 10, timestamp: p.timestamp ?? Date.now(), segment: p.segment ?? 0 })), finish: true, title: activity.title || '', note: activity.note || '',
    });
  }
  return liveMutation('/api/activities/manual', 'POST', {
    id: activity.id, user_id: me.id, challenge_id: activity.challengeId, activity_type: activity.type || 'walk',
    distance_km: activity.distanceKm, duration_s: activity.duration || 1, date: activity.date, title: activity.title || '', note: activity.note || '',
  });
}
export const approveActivity = id => liveMutation('/api/activities/' + encodeURIComponent(id) + '/moderate', 'POST', { action: 'approve' });
export const rejectActivity = (id, reason) => liveMutation('/api/activities/' + encodeURIComponent(id) + '/moderate', 'POST', { action: 'reject', reason });
export const excludeActivity = (id, reason) => liveMutation('/api/activities/' + encodeURIComponent(id) + '/moderate', 'POST', { action: 'exclude', reason });
export const deleteActivity = id => request('/api/activities/' + encodeURIComponent(id) + '?user_id=' + (token()?.token ? '' : ''), { method: 'DELETE' });
export const editActivity = payload => liveMutation('/api/activities/' + encodeURIComponent(payload.id), 'PATCH', payload);

export async function getActivities({ managed = false, userId, challengeId } = {}) {
  const me = await getSession(); if (!me) return [];
  const params = new URLSearchParams();
  if (managed && ['admin', 'superadmin', 'organiser'].includes(me.role)) { /* organiser view */ }
  else params.set('user_id', userId || me.id);
  if (challengeId) params.set('challenge_id', challengeId);
  const list = await request('/api/activities?' + params.toString());
  return list.map(a => ({ ...a, distanceKm: a.distance_m / 1000, challengeTitle: a.challenge_name, participantName: a.participant_name, type: a.activity_type }));
}

// ---- read-only dashboards (server-computed) ----
export async function getLeaderboard(id) {
  const rows = await request('/api/leaderboards/' + encodeURIComponent(id));
  return (rows || []).map(r => ({
    ...r,
    rank: r.rank ?? 0,
    name: r.name || 'Walker',
    sample: false,
    progress: {
      distanceKm: r.progress?.distance_km ?? 0,
      percent: r.progress?.percent ?? 0,
      complete: !!r.progress?.complete,
      activityCount: r.progress?.activity_count ?? 0,
    },
  }));
}
export async function getWeeklyGoal() {
  const me = await getSession();
  const target = me?.weeklyTargetKm || 20;
  const acts = me ? await getActivities({ userId: me.id }) : [];
  const start = new Date(); start.setHours(0,0,0,0); start.setDate(start.getDate() - ((start.getDay()+6)%7));
  const days = Array.from({length:7},(_,i)=>{const d=new Date(start);d.setDate(d.getDate()+i);return {date:d.toLocaleDateString('en-CA'),label:d.toLocaleDateString('en',{weekday:'short'}),km:0,start:d.getTime()};});
  for (const a of acts.filter(x=>x.status==='accepted')) {
    const t = new Date(a.date).getTime();
    for (const day of days) {
      const end = day.start + 86400000;
      if (t >= day.start && t < end) day.km += a.distanceKm || a.distance_m/1000 || 0;
    }
  }
  const distance = days.reduce((n,d)=>n+d.km,0);
  return { days, target, distance, percent: target?Math.min(100,100*distance/target):0, complete: !!target && distance>=target };
}
export const updateProfile = payload => liveMutation('/api/profiles/' + encodeURIComponent(payload.id || ''), 'PATCH', { name: payload.name });
export async function getSettings() { return { streetViewKey: '', backend: 'live', wordpressMapping: { status: 'read-only-public' }, policyVersion: 'yvr-2026-09' }; }
export async function getQueue() { return []; }
export const retrySync = async () => {};
export const syncNow = async () => {};
export function startSync() { return () => {}; }
export async function saveRecording(recording) { try { localStorage.setItem('yvr_recording_' + recording.userId, JSON.stringify(recording)); } catch {} }
export async function getRecording() { const me = await getSession(); if (!me) return null; try { return JSON.parse(localStorage.getItem('yvr_recording_' + me.id) || 'null'); } catch { return null; } }
export async function discardRecording() { const me = await getSession(); if (me) localStorage.removeItem('yvr_recording_' + me.id); }
export async function uploadImage(file) {
  const body = new FormData(); body.append('file', file);
  const t = token();
  const res = await fetch(API_BASE + '/api/media', { method: 'POST', headers: t ? { Authorization: 'Bearer ' + t.token } : {}, body });
  if (!res.ok) throw new Error('Upload failed');
  const j = await res.json();
  return API_BASE + j.url;
}
export async function getStats() { const me = await getSession(); if (!me) return {}; return request('/api/organiser/stats').catch(() => ({ signups: 0, activeWalkers: 0, totalKm: 0, popular: [] })); }
export async function getDirectory() { return []; }
export async function getMedals() { return []; }
export async function getAuditJournal() { return []; }
export async function getUnreadJourney() { return []; }
export const markJourneySeen = async () => {};
export const issueMedal = async () => {};
export const updateShipment = async () => {};
export const requestWordPressSync = async () => {};
